// INVENTORY-001 client: stock levels (projection), adjustments (→ InventoryDO) and locations.
// Shapes mirror packages/domain/inventory/src/http.ts and locations.ts.
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { i18n, t } from "@ady/i18n";
import { formatMoney } from "@ady/format";
import { ApiError, request } from "../api/client";
import { errorMessage } from "../api/errors";
import type { Page } from "../api/types";

export type StockState = "in_stock" | "low" | "out_of_stock" | "untracked";

export interface StockRow {
  variant_id: string;
  product_id: string;
  product_title: string;
  variant_title: string;
  sku: string | null;
  barcode: string | null;
  track_inventory: boolean;
  allow_backorder: boolean;
  on_hand: number;
  reserved: number;
  available: number;
  state: StockState;
  levels: { location_id: string; on_hand: number; reserved: number; available: number }[];
}

export type LocationKind = "warehouse" | "outlet" | "online_pool";
export interface Location {
  id: string;
  code: string;
  name: string;
  kind: LocationKind;
  is_fulfilment: boolean;
  active: boolean;
  is_default: boolean;
  version: number;
}

/** One stock change (INVENTORY-001 movements), newest first. */
export type MovementReason =
  | "sale"
  | "return"
  | "adjust"
  | "transfer_out"
  | "transfer_in"
  | "stock_in"
  | "count"
  | "offline_conflict";
export interface Movement {
  id: string;
  product_id: string;
  variant_id: string;
  location_id: string;
  delta: number;
  reason: MovementReason;
  source_type: string;
  source_id: string;
  actor_type: string;
  actor_id: string | null;
  seq: number;
  occurred_at: string;
}

export interface AdjustRequest {
  variant_id: string;
  location_id?: string;
  delta?: number;
  set_to?: number;
  reason: "adjust" | "stock_in" | "count" | "return";
  note?: string;
}

const org = (orgId: string) => `/orgs/${encodeURIComponent(orgId)}`;

export const inventoryApi = {
  levels: (
    orgId: string,
    storeId: string,
    q: { q?: string; low?: boolean; location?: string; cursor?: string },
  ) =>
    request<Page<StockRow>>(
      "GET",
      `${org(orgId)}/stores/${encodeURIComponent(storeId)}/inventory`,
      {
        query: {
          q: q.q || undefined,
          low: q.low ? "true" : undefined,
          location: q.location || undefined,
          cursor: q.cursor,
          limit: 50,
        },
      },
    ),
  adjust: (orgId: string, body: AdjustRequest, idempotencyKey: string) =>
    request<{ variant_id: string; location_id: string; on_hand: number }>(
      "POST",
      `${org(orgId)}/inventory/adjustments`,
      { body, idempotencyKey },
    ),
  /** Stock history for one item (and location), newest first. */
  movements: (orgId: string, q: { variant?: string; location?: string; cursor?: string }) =>
    request<Page<Movement>>("GET", `${org(orgId)}/inventory/movements`, {
      query: { variant: q.variant, location: q.location || undefined, cursor: q.cursor, limit: 50 },
    }),
  locations: (orgId: string) => request<Page<Location>>("GET", `${org(orgId)}/locations`),
  createLocation: (
    orgId: string,
    body: { code: string; name: string; kind: LocationKind; is_fulfilment?: boolean },
    idempotencyKey: string,
  ) => request<Location>("POST", `${org(orgId)}/locations`, { body, idempotencyKey }),
  updateLocation: (
    orgId: string,
    id: string,
    version: number,
    body: { name?: string; active?: boolean; is_fulfilment?: boolean; is_default?: true },
  ) =>
    request<Location>("PATCH", `${org(orgId)}/locations/${encodeURIComponent(id)}`, {
      body,
      ifMatch: version,
    }),
};

export const opsKeys = {
  levels: (orgId: string, storeId: string, q: object) =>
    ["org", orgId, "store", storeId, "inventory", q] as const,
  inventory: (orgId: string, storeId: string) =>
    ["org", orgId, "store", storeId, "inventory"] as const,
  locations: (orgId: string) => ["org", orgId, "locations"] as const,
  movements: (orgId: string, variant: string) =>
    ["org", orgId, "inventory-movements", variant] as const,
  profiles: (orgId: string, storeId: string) =>
    ["org", orgId, "store", storeId, "delivery"] as const,
  sales: (orgId: string, storeId: string) => ["org", orgId, "store", storeId, "pos-sales"] as const,
};

export const useStockLevels = (
  orgId: string,
  storeId: string,
  q: { q?: string; low?: boolean; location?: string },
  enabled: boolean,
) =>
  useInfiniteQuery({
    queryKey: opsKeys.levels(orgId, storeId, q),
    queryFn: ({ pageParam }) => inventoryApi.levels(orgId, storeId, { ...q, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled,
  });

export const useLocations = (orgId: string, enabled = true) =>
  useQuery({
    queryKey: opsKeys.locations(orgId),
    queryFn: () => inventoryApi.locations(orgId),
    enabled,
  });

/** Operations error codes → catalogue copy; everything else falls back to the shared mapping. */
export function opsErrorMessage(err: unknown): string {
  if (!(err instanceof ApiError)) return errorMessage(err);
  const d = err.details ?? {};
  switch (err.code) {
    case "inventory.insufficient_stock": {
      const first = (d.shortfall as { available?: number }[] | undefined)?.[0];
      return first?.available !== undefined
        ? t("operations.errors.insufficientStock", { available: first.available })
        : t("operations.errors.insufficientStockGeneric");
    }
    case "inventory.below_reserved":
      return t("operations.errors.belowReserved");
    case "checkout.item_unavailable":
      return t("operations.errors.itemUnavailable");
    case "payment.tender_short":
      return t("operations.errors.tenderShort", { due: formatMoney(Number(d.due_paisa ?? 0)) });
    case "payment.tender_exceeds_total":
      return t("operations.errors.tenderExceeds");
    case "checkout.address_invalid":
      return t("operations.errors.addressInvalid");
    case "checkout.address_unserviceable":
      return t("operations.errors.unserviceable");
    case "payment.method_ineligible":
      return t("operations.errors.codIneligible");
    case "precondition.version_mismatch":
      return t("operations.errors.versionMismatch");
    case "inventory.default_location":
      return t("operations.errors.defaultLocation");
    case "shipping.default_profile":
      return t("operations.errors.defaultProfile");
    case "validation.invalid":
      if (err.fields?.code === "taken") return t("operations.errors.duplicateCode");
      return errorMessage(err);
    default:
      return errorMessage(err);
  }
}

export const isPending = (err: unknown) =>
  err instanceof ApiError && err.code === "dependency.activation_required";

export const stateLabel = (s: StockState) =>
  i18n.tDynamic(`operations.inventory.state.${s}`, "operations.inventory.state.untracked");
